import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, FileText, ChevronRight } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { adminApi, type ComplianceDocMeta, type ComplianceDocStatus } from '@/services/adminApi';

const STATUS_VARIANT: Record<ComplianceDocStatus, { label: string; className: string }> = {
  draft: { label: 'Bozza', className: 'bg-muted text-muted-foreground' },
  in_review: { label: 'In revisione', className: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Approvato', className: 'bg-emerald-100 text-emerald-800' },
};

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' });
}

export default function AdminCompliancePage() {
  const [items, setItems] = useState<ComplianceDocMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminApi.compliance
      .list()
      .then((res) => {
        if (!cancelled) setItems(res.items);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Errore');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="max-w-4xl">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="h-5 w-5 text-primary" />
        <h1 className="text-xl font-semibold">Compliance</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Documenti AI Act / GDPR del provider (G-One). Versionati in Firestore, esportabili in PDF / DOCX / Excel.
      </p>

      {loading && <p className="text-sm text-muted-foreground">Caricamento…</p>}
      {error && (
        <p className="text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2">
          {error}
        </p>
      )}

      {!loading && !error && items.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Nessun documento. Esegui lo script di seed{' '}
            <code className="text-xs">_seed-compliance-docs.ts</code>.
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {items.map((doc) => {
          const status = STATUS_VARIANT[doc.status];
          return (
            <Link key={doc.slug} to={`/admin/compliance/${doc.slug}`}>
              <Card className="hover:border-primary/40 transition-colors">
                <CardContent className="py-4 flex items-center gap-3">
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{doc.title}</p>
                    <p className="text-xs text-muted-foreground">
                      v{doc.currentVersion} · agg. {fmtDate(doc.updatedAt)}
                    </p>
                  </div>
                  <Badge className={status.className} variant="secondary">
                    {status.label}
                  </Badge>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
